import React from "react";
//#region types
import {
  EditableFormInnerProps,
  ExpandableSectionProps,
  FormSectionObject,
  GenericFormSection,
} from "models/propTypes/genericFormProps";
import { FormEnums } from "models/enums";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Grid, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import useCheckVisibility from "./hooks/useCheckVisibility";
import useFormSectionObject from "./hooks/useFormSectionObject";
import Icon from "../icon";
import GenericFormInner from "./genericFormInner";
import { useFormikContext } from "formik";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";
import clsx from "clsx";

function EditableFormInnerExpandable<T extends Record<string, any>>(props: EditableFormInnerProps<T>) {
  const { validationSchema, initialValues } = props;
  const { values, submitCount, isSubmitting } = useFormikContext();

  const fields = props.fields as GenericFormSection<T>[];
  const { getFieldHidden } = useCheckVisibility<T>();
  const { getFormSectionValues } = useFormSectionObject<T>(validationSchema);

  const [expandedSections, setExpandedSections] = React.useState<Set<number>>();
  const [previouslyExpandedSections, setPreviouslyExpandedSections] = React.useState<Set<number>>();

  const expansionPanelObjAll = fields.map((field, index) => ({
    index: index,
    field: field,
    hidden: getFieldHidden(field.hidden),
    sectionValues: getFormSectionValues(field),
  }));

  const expansionPanelObj = expansionPanelObjAll.filter((e) => !e.hidden);

  const getSummary = (
    sectionValues: FormSectionObject,
    summaryFormatter?: ((values: any) => string | undefined) | string,
    summaryType?: FormEnums.ExpandableFormSummaryType
  ) => {
    const inputsCount = sectionValues.hasValueCount;
    const totalItems = sectionValues.total;

    switch (summaryType) {
      case FormEnums.ExpandableFormSummaryType.SELECTEDCOUNT:
        return inputsCount > 0 ? (
          <label>{`${inputsCount}/${totalItems}`}</label>
        ) : (
          <label>({LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NotSelected)})</label>
        );
      default:
        return summaryFormatter ? (
          typeof summaryFormatter === "string" ? (
            summaryFormatter.length > 0 ? (
              <label>{LocalizationHelperInstance.getFormattedMessage(summaryFormatter)}</label>
            ) : undefined
          ) : (
            summaryFormatter(values)
          )
        ) : undefined;
    }
  };

  const onChange = (panel: number) => (event: React.SyntheticEvent | undefined, isExpanded: boolean) => {
    const updatedExpandedSections = new Set(expandedSections);
    if (isExpanded) updatedExpandedSections.add(panel);
    else updatedExpandedSections.delete(panel);
    setExpandedSections(updatedExpandedSections);
    setPreviouslyExpandedSections(new Set([...Array.from(previouslyExpandedSections ?? []), panel]));
  };

  React.useEffect(() => {
    if (submitCount > 0 && !isSubmitting) {
      setExpandedSections(
        new Set(
          Array.from(expandedSections ?? new Set<number>()).concat(
            expansionPanelObjAll.filter((x) => x.sectionValues.hasErrors).map((x) => x.index)
          )
        )
      );
    }
  }, [submitCount, isSubmitting]);

  React.useEffect(() => {
    const sections = expansionPanelObjAll
      .filter((currentPanel) => {
        if (previouslyExpandedSections?.has(currentPanel.index))
          return Array.from(expandedSections ?? []).includes(currentPanel.index);

        const section = currentPanel.field;
        const extraProps = (section.extraProps ?? {}) as ExpandableSectionProps;

        const sectionValues = currentPanel.sectionValues;
        const hasRequired = sectionValues.hasRequired;

        const initiallyOpen =
          (typeof extraProps.initiallyOpen === "function"
            ? extraProps.initiallyOpen(initialValues)
            : extraProps.initiallyOpen) || hasRequired;

        return initiallyOpen;
      })
      .map((x) => x.index);

    setExpandedSections(new Set(sections));
    if (expandedSections === undefined) {
      setPreviouslyExpandedSections(new Set(sections));
    } else {
      setPreviouslyExpandedSections(new Set([...Array.from(previouslyExpandedSections ?? []), ...sections]));
    }
  }, [validationSchema]);

  return (
    <Grid container direction={"column"} className={cssExports.expandableForm}>
      {expansionPanelObj.map((currentPanel) => {
        const section = currentPanel.field;
        const extraProps = (section.extraProps ?? {}) as ExpandableSectionProps;
        const className = section.className ?? "";

        const sectionValues = currentPanel.sectionValues;
        const summary = getSummary(sectionValues, extraProps.summaryFormatter, extraProps.summaryType);

        return (
          <Accordion
            key={`sectionHeader-${currentPanel.index}`}
            className={clsx(className, cssExports.expansionPanel)}
            disableGutters
            expanded={extraProps.alwaysOpen || (expandedSections && expandedSections.has(currentPanel.index)) || false}
            onChange={onChange(currentPanel.index)}
          >
            {((section.label && section.label.length > 0) || summary) && (
              <AccordionSummary
                classes={{
                  root: cssExports.expansionPanelSummary,
                  content: cssExports.expansionPanelSummaryContent,
                  expandIconWrapper: cssExports.expansionPanelIcon,
                  expanded: cssExports.expansionPanelExpandedSummary,
                }}
                expandIcon={extraProps.alwaysOpen ? null : <ExpandMoreIcon />}
              >
                <Grid container alignItems="center" spacing={1}>
                  {section.label && (
                    <Grid item container xs className={section.labelSmall ? cssExports.expansionPanelTitleSmall : ""}>
                      <Grid item container alignItems="center">
                        {section.icon ? (
                          <Icon icon={section.icon} className={cssExports.expansionPanelTitleIcon} />
                        ) : (
                          ""
                        )}
                        {LocalizationHelperInstance.getFormattedMessage(section.label, section.label)}
                      </Grid>
                    </Grid>
                  )}

                  <Grid item className={cssExports.expansionPanelSubText}>
                    {summary ??
                      (sectionValues.hasRequired ? (
                        <></>
                      ) : (
                        <OptionalIndicator
                          hasRequired={sectionValues.hasRequired}
                          hideOptionalIndicator={props.hideOptionalIndicator}
                        />
                      ))}
                  </Grid>
                </Grid>
              </AccordionSummary>
            )}
            <AccordionDetails
              classes={{
                root: cssExports.expansionPanelDetails,
              }}
            >
              {
                <GenericFormInner<T>
                  {...props}
                  key={`expandableSection-${currentPanel.index}`}
                  fields={section.innerFields}
                  inline={section.inline ?? props.inline}
                  enableControlLables={section.enableControlLables ?? props.enableControlLables}
                  formGridLayout={section.formGridLayout ?? props.formGridLayout}
                />
              }
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Grid>
  );
}

function OptionalIndicator(props: { hasRequired: boolean; hideOptionalIndicator?: boolean }) {
  return !props.hasRequired && !props.hideOptionalIndicator ? (
    <label>{LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Optional)}</label>
  ) : (
    <></>
  );
}

export default EditableFormInnerExpandable;
